<template>
  <div id="elevation-chart-container">
    <div v-if="!elevationData" class="text-center p-5">
      <div class="spinner-border" role="status" v-if="!loadError" ref="loadSpinner">
        <span class="sr-only">Loading...</span>
      </div>
      <div v-else ref="errorMessage">
        Elevation chart could not be loaded: received unexpected response from server
        with status {{loadError.status}}. Please try again later.
      </div>
    </div>
    <canvas v-else id="elevation-chart" ref="elevation-chart"></canvas>
  </div>
</template>

<script>
import Chart from 'chart.js';
import Color from 'color';
import { chunk, meanBy, merge, get, isEmpty, has } from 'lodash';
import unitConversion from "../../utilities/unit_conversion";
import { calculateStepSize } from "./utilities/chart_helpers";
import { pointFeatureToPopupContent } from "utilities/mapbox_style_adapters";

const DEFAULT_OPTIONS = {
  metric: false,
  text: {
    xAxis: "Distance",
    yAxis: "Elevation"
  },
  axes: {
    x: {
      orientation: 'normal'
    }
  },
  elevationDisplay: {
    backgroundColor: Color("#B2BCC2").whiten(0.3).hex(),
    borderColor: "#B2BCC2",
    fill: true,
    poiBorderColor: "#fff",
    poiBackgroundColor: '#2980B9',
    poiHoverBorderColor: '#000',
    poiHoverBackgroundColor: '#fff'
  }
};
const PERFORMANT_POINT_COUNT = 300;

export default {
  props: ['options', 'routeId', 'pointFeatures'],

  watch: {
    routeId: function () {
      this.fetchElevationPoints();
    },

    elevationDataAndOptions: function () {
      if (this.chart) this.chart.destroy();

      // Ensures that canvas will actually be showing when we set the chart.
      this.$nextTick(() => {
        this.setChart();
      });
    },

    pointDataAndOptions: function () {
      if (this.chart) this.chart.destroy();

      // Ensures that canvas will actually be showing when we set the chart.
      this.$nextTick(() => {
        this.setChart();
      });
    }
  },

  computed: {
    chartOptions() {
      return {
        maintainAspectRatio: false,
        responsive: true,
        animation: false,
        elements: { point: { hitRadius: 0, hoverRadius: 0} },
        scaleShowVerticalLines: false,
        legend: { display: false },
        events: ["mousemove"],
        onHover: (event) => {
          if (event.type == "mousemove") {
            const elements = this.chart.getElementsAtEventForMode(
              event,
              'nearest',
              { intersect: false, axis: 'x' },
              false
            );
            if (!elements.length) return;

            // Note: Chartjs returns strange results when hovering over a POI. It
            // claims to show a elevation dataset point, but uses the same index applicable
            // to the overlapping POI. So, if we have multiple elements, let's just use
            // data from the POI itself. See issue #597.
            let activePoint;
            const poiDatasetIndex = 1;
            if (elements.length > 1) {
              activePoint = elements.find(el => el._datasetIndex == poiDatasetIndex);
              // More oddities. Sometimes multiple elements are returned, but only
              // because they are elevation dataset elements that are stacked upon one
              // another (i.e., have the same x value, but different y values). Likely
              // a data problem with us.
              if (!activePoint) activePoint = elements[0];
            } else {
              activePoint = elements[0];
            }

            const dataPoint = this.chart.data.datasets[activePoint._datasetIndex].data[activePoint._index],
              xScale = activePoint._xScale,
              yScale = activePoint._yScale,
              left = this.hasReverseXAxes ? 'max' : 'min',
              right = this.hasReverseXAxes ? 'min' : 'max';

            if (
              event.offsetX < xScale.getPixelForValue(xScale[left]) ||
              event.offsetX > xScale.getPixelForValue(xScale[right]) ||
              event.offsetY > yScale.getPixelForValue(yScale.min) ||
              event.offsetY < yScale.getPixelForValue(yScale.max)
            ) return;

            this.$emit('chart-canvas-mousemove', {
              chart: this.chart,
              distance: {
                value: dataPoint.x,
                text: `${(Math.round(100 * dataPoint.x) / 100).toLocaleString()} ${this.distanceUnitText}`,
              },
              elevation: {
                value: dataPoint.y,
                text: `${dataPoint.y.toLocaleString()} ${this.elevationUnitText}`
              },
              grade: {
                value: dataPoint.grade,
                text: dataPoint.grade != null ? `${Math.round(dataPoint.grade)}%` : ""
              },
              point: [dataPoint.lng, dataPoint.lat],
              mousemoveEvent: event,
              graphBounds: {
                x: {
                  min: xScale.getPixelForValue(xScale.min),
                  max: xScale.getPixelForValue(xScale.max)
                },
                y: {
                  min: yScale.getPixelForValue(yScale.min),
                  max: yScale.getPixelForValue(yScale.max)
                }
              }
            });
          }
        },
        scales: {
          xAxes: [{
            id: 'distance-axis',
            type: "linear",
            position: "bottom",
            gridLines: {
              display: false
            },
            ticks: {
              reverse: this.hasReverseXAxes,
              max: this.elevationDataAndOptions.data[this.elevationDataAndOptions.data.length - 1].x,
              maxTicksLimit: 8,
              stepSize: calculateStepSize(
                0, this.elevationDataAndOptions.data[this.elevationDataAndOptions.data.length - 1].x, 8
              ),
              callback: (value, index, values) => {
                const matchingIndex = this.hasReverseXAxes ? 0 : values.length - 1
                if (index == matchingIndex) return null; // Skip last tick
                return `${value.toLocaleString()} ${this.distanceUnitText}`
              }
            },
            scaleLabel: {
              display: true,
              labelString: this.normalizedOptions.text.xAxis,
              fontStyle: 'bold'
            }
          }],
          yAxes: [{
            id: 'elevation-axis',
            ticks: {
              maxTicksLimit: 8,
              max: this.normalizedElevationMax,
              beginAtZero: false,
              stepSize: calculateStepSize(
                this.extremes.elevation.min, this.normalizedElevationMax, 8
              ),
              callback: (value, index, values) => {
                if (index == 0) return null; // Hide last Y tick
                return `${value} ${this.elevationUnitText}`
              }
            },
            scaleLabel: {
              display: true,
              labelString: this.normalizedOptions.text.yAxis,
              fontStyle: 'bold'
            }
          }]
        },
        tooltips: {
          enabled: false,
          custom: function(tooltipModel) {
            // Tooltip Element
            let tooltipEl = document.getElementById('chartjs-tooltip');

            // Create element on first render
            if (!tooltipEl) {
              tooltipEl = document.createElement('div');
              tooltipEl.id = 'chartjs-tooltip';
              tooltipEl.style.position = 'absolute';
              tooltipEl.style.color = tooltipModel.bodyFontColor;
              tooltipEl.style.backgroundColor = tooltipModel.backgroundColor;
              tooltipEl.style.fontFamily = tooltipModel._bodyFontFamily;
              tooltipEl.style.fontStyle = tooltipModel._bodyFontStyle;
              tooltipEl.style.fontSize = tooltipModel.bodyFontSize + 'px';
              tooltipEl.style.padding = tooltipModel.yPadding + 'px ' + tooltipModel.xPadding + 'px';
              tooltipEl.style.pointerEvents = 'none';
              document.body.appendChild(tooltipEl);
            }

            // Hide if no tooltip
            if (tooltipModel.opacity === 0) {
              tooltipEl.style.opacity = 0;
              return;
            } else {
              tooltipEl.style.opacity = 1;
            }

            // Set caret Position
            tooltipEl.classList.remove('above', 'below', 'no-transform');
            if (tooltipModel.yAlign) {
              tooltipEl.classList.add(tooltipModel.yAlign);
            } else {
              tooltipEl.classList.add('no-transform');
            }

            // Set Text
            const datasetIndex = tooltipModel.dataPoints[0].datasetIndex,
                  dataIndex = tooltipModel.dataPoints[0].index;
            const dataPoint = this._chart.data.datasets[datasetIndex].data[dataIndex];
            tooltipEl.innerHTML = dataPoint.tooltipProperties.content;

            // `this` will be the overall tooltip
            const position = this._chart.canvas.getBoundingClientRect();
            const padding = 15;
            if (tooltipModel.caretY <= (position.bottom - position.top) / 2) {
              tooltipEl.style.top = position.top + window.pageYOffset + tooltipModel.caretY + 'px';
              tooltipEl.style.bottom = null;
            } else {
              tooltipEl.style.bottom = position.bottom - (position.top + window.pageYOffset + tooltipModel.caretY) + 'px';
              tooltipEl.style.top = null;
            }
            if (tooltipModel.caretX <= (position.right - position.left) / 2) {
              tooltipEl.style.left = position.left + window.pageXOffset + tooltipModel.caretX + padding + 'px';
              tooltipEl.style.right = null;
            } else {
              tooltipEl.style.right = position.right - window.pageXOffset - tooltipModel.caretX + padding + 'px';
              tooltipEl.style.left = null;
            }
          }
        }
      };
    },

    distanceUnitText() {
      if (this.normalizedOptions.metric) return 'km';

      return 'mi';
    },

    elevationUnitText() {
      if (this.normalizedOptions.metric) return 'm';

      return 'ft';
    },

    elevationDataAndOptions() {
      return {
        label: this.normalizedOptions.text.yAxis,
        backgroundColor: this.normalizedOptions.elevationDisplay.backgroundColor,
        borderColor: this.normalizedOptions.elevationDisplay.borderColor,
        data: this.elevationData,
        borderWidth: 4,
        cubicInterpolationMode: "default",
        fill: this.normalizedOptions.elevationDisplay.fill,
        lineTension: 0,
        order: 1,
        pointRadius: 0
      }
    },

    extremes() {
      let data = this.elevationDataAndOptions.data,
          dMax = data[0].x,
          dMin = data[0].x,
          eMax = data[0].y,
          eMin = data[0].y;

      data.forEach(elevationPoint => {
        if (elevationPoint.x < dMin) dMin = elevationPoint.x
        if (elevationPoint.x > dMax) dMax = elevationPoint.x
        if (elevationPoint.y < eMin) eMin = elevationPoint.y
        if (elevationPoint.y > eMax) eMax = elevationPoint.y
      });

      return {
        distance: {
          max: dMax,
          min: dMin
        },
        elevation: {
          max: eMax,
          min: eMin
        }
      }
    },

    normalizedElevationMax() {
      let key = this.normalizedOptions.metric ? 'meters' : 'feet';
      if (this.extremes.elevation.max < this.CONSTANTS.minYAxisHeight[key])
        return this.CONSTANTS.minYAxisHeight[key];
      return this.extremes.elevation.max + Math.min(Math.round(this.extremes.elevation.max/10), 100);
    },

    normalizedOptions() {
      return merge({}, DEFAULT_OPTIONS, this.options);
    },

    hasReverseXAxes() {
      return this.normalizedOptions.axes.x.orientation == 'reverse';
    },

    pointDataAndOptions() {
      return {
        label: "Points of interest",
        pointRadius: 6,
        pointBackgroundColor: this.normalizedOptions.elevationDisplay.poiBackgroundColor,
        pointBorderColor:  this.normalizedOptions.elevationDisplay.poiBorderColor,
        pointBorderWidth: 2,
        pointHitRadius: 6,
        pointHoverBackgroundColor:  this.normalizedOptions.elevationDisplay.poiHoverBackgroundColor,
        pointHoverBorderColor:  this.normalizedOptions.elevationDisplay.poiHoverBorderColor,
        pointHoverBorderWidth: 3,
        pointHoverRadius: 8,
        data: this.pointFeaturesAsChartData,
        borderWidth: 0,
        cubicInterpolationMode: "monotone",
        fill: false,
        lineTension: 0,
        order: 0,
        showLine: false
      };
    },

    pointFeaturesAsChartData() {
      if (!this.elevationData || !this.pointFeatures || !this.pointFeatures.length) return [];

      const pointsWithVisits = this.pointFeatures.filter(feature => {
        return !isEmpty(get(feature, 'properties.visit_data', []));
      });
      const visits = pointsWithVisits.map(feature => {
        return feature.properties.visit_data.map(visit => {
          const x = unitConversion(visit.distance_along_path, {
            from: 'm',
            to: this.normalizedOptions.metric ? 'km' : 'mi'
          });
          const y = Math.round(this.getYCoordinateFromDistanceAlongPath(x));

          return {
            lng: feature.geometry.coordinates[0],
            lat: feature.geometry.coordinates[1],
            x: x,
            y: y,
            tooltipProperties: {
              icon: feature.properties.icon,
              title: feature.properties.title,
              content: pointFeatureToPopupContent(feature, { excludeCoordinateLink: true }),
              notes: get(feature.properties, 'form.marker_notes'),
              pacer: get(feature.properties, 'form.marker_pacer'),
              dropbag: get(feature.properties, 'form.marker_drop_bag'),
              cutoff: get(feature.properties, 'form.marker_cutoff')
            }
          };
        });
      }).flat();

      return visits.filter(visit => visit != null);
    }
  },

  created() {
    this.fetchElevationPoints();
  },

  data() {
    return {
      chart: null,
      CONSTANTS: {
        minYAxisHeight: {
          meters: 100,
          feet: 300
        }
      },
      elevationData: null,
      loadError: null,
      requestCount: 0
    };
  },

  methods: {
    elevationDataNormalizer(dataPoints) {
      const keys = ['x', 'y', 'lat', 'lng'];
      if (has(dataPoints[0], 'grade')) keys.push('grade');

      const chunkedDataPoints = chunk(
        dataPoints,
        Math.ceil(dataPoints.length / PERFORMANT_POINT_COUNT)
      )
      return chunkedDataPoints.map((chunk) => {
        const averagedPoint = keys.reduce(
          (obj, key) => ({ ...obj, [key]: meanBy(chunk, o => o[key])}),
          {}
        )
        if (this.normalizedOptions.metric) {
          averagedPoint.x = unitConversion(averagedPoint.x, { from: 'm', to: 'km' });
        } else {
          averagedPoint.x = unitConversion(averagedPoint.x, { from: 'm', to: 'mi' });
          averagedPoint.y = Math.round(
            unitConversion(averagedPoint.y, { from: 'm', to: 'ft' })
          );
        }

        return averagedPoint;
      });
    },

    getYCoordinateFromDistanceAlongPath(distanceAlongPath) {
      let point1 = null;
      let point2 = null;

      for (let i = 0; i < this.elevationDataAndOptions.data.length; i++) {
        const point = this.elevationDataAndOptions.data[i];

        if (!point1 || point.x <= distanceAlongPath) point1 = point;
        if (point.x >= distanceAlongPath) point2 = point;
        if (point1 && point2)  break;
      }

      if (!point2) point2 = point1;
      const base = point2.x - point1.x;
      // return y coord based on ratio distance between coords
      if (base == 0) return point1.y

      const ratio = (distanceAlongPath - point1.x) / base;
      const yDiff = point2.y - point1.y;
      return point1.y + (yDiff * ratio);
    },

    async fetchElevationPoints(lastError) {
      if (this.requestCount > 9) {
        this.loadError = lastError.response;
        return;
      }

      this.requestCount += 1;

      try {
        const response = await this.$axios.get(`/api/routes/${this.routeId}/elevation_data`);

        this.elevationData = this.elevationDataNormalizer(response.data.elevation_data);
      } catch (error) {
        if (!error.response) throw error;

        switch (error.response.status) {
          case 404:
          case 503:
          case 408:
          case 429:
            return await setTimeout(() => { this.fetchElevationPoints(error) } , 3000);
            break;
          default:
            this.loadError = error.response;
        }
      }
    },

    setChart() {
      const element = this.$refs['elevation-chart'];
      this.chart = new Chart(
        element,
        {
          type: 'line',
          data: {
            datasets: [this.elevationDataAndOptions, this.pointDataAndOptions]
          },
          options: this.chartOptions
        },
      );
    }
  }
}
</script>
